import {
  DownOutlined,
  EnvironmentOutlined,
  FlagOutlined,
  GlobalOutlined,
  LoginOutlined,
} from "@ant-design/icons";
import { Button, Dropdown, Layout, Menu, message } from "antd";
import * as React from "react";
import { logout, verifyCookie } from "./API/Auth";
import "./App.css";
import { LoginModal } from "./Login/LoginModal";
import { TableTab } from "./TableTab/TableTab";

import logo_indust from "./assets/logo_indust.png";
import logo_cost from "./assets/logo_cost.png";
import flag_eu from "./assets/flag_eu.svg";
import model_icon from "./assets/model-tab-icon.svg";
import marine_icon from "./assets/marine-tab-icon.svg";
import { ChangePasswordModal } from "./Login/ChangePasswordModal";

const { Header, Content, Footer } = Layout;

interface AppState {
  /** Is the login modal visible? */
  loginVisible: boolean;
  /** Is the change password modal visible? */
  passwdVisible: boolean;
  /** User info. Undefined when not logged in */
  user?: {
    userId: number;
    fullName: string;
    email: string;
    isAdmin: boolean;
  };
  /** Currently selected tab */
  activeTabKey: string;
}

class App extends React.Component<{}, AppState> {
  constructor(props: {}) {
    super(props);

    // Check local storage for an old session id
    const userIdString = localStorage.getItem("userId");
    const userId = userIdString !== null ? parseInt(userIdString) : -1;
    const fullName = localStorage.getItem("fullName");
    const email = localStorage.getItem("email");
    const isAdmin = localStorage.getItem("isAdmin") === "true";

    if (fullName === null || email === null || isAdmin === null) {
      this.state = {
        activeTabKey: "satellites",
        loginVisible: false,
        passwdVisible: false,
        user: undefined,
      };
    } else {
      this.state = {
        activeTabKey: "satellites",
        loginVisible: false,
        passwdVisible: false,
        user: { userId, fullName, email, isAdmin },
      };
    }
  }

  public componentDidMount() {
    this.verifyId();
  }

  public render() {
    // Prepare login header
    let loginHeader: JSX.Element;

    if (this.state.user !== undefined) {
      const { fullName } = this.state.user;
      loginHeader = (
        <div className="header-login">
          <span className="header-greeting">Logged in as {fullName}</span>
          <Dropdown.Button
            icon={<DownOutlined />}
            onClick={this.onLogout}
            overlay={
              <Menu>
                <Menu.Item
                  onClick={() => this.setState({ passwdVisible: true })}
                >
                  Change Password
                </Menu.Item>
              </Menu>
            }
          >
            {" "}
            Logout{" "}
          </Dropdown.Button>
        </div>
      );
    } else {
      loginHeader = (
        <Button
          style={{ color: "#fff"}}
          type="ghost"
          icon={<LoginOutlined />}
          onClick={this.showLoginForm}
        >
          Admin Login
        </Button>
      );
    }

    // TODO The custom SVG icons should be handled by webpack.
    return (
      <Layout className="App">
        <Header className="header">
          <h1>Dust Catalogue</h1>
          <Menu
            className="header-menu"
            theme="dark"
            mode="horizontal"
            selectedKeys={[this.state.activeTabKey]}
            onSelect={this.onTabSelected}
          >
            <Menu.Item key="satellites">
              <GlobalOutlined />
              <span className="nav-text">Satellites</span>
            </Menu.Item>
            <Menu.Item key="groundBased">
              <EnvironmentOutlined />
              <span className="nav-text">Ground-based</span>
            </Menu.Item>
            <Menu.Item key="campaigns">
              <FlagOutlined />
              <span className="nav-text">Campaigns</span>
            </Menu.Item>
            <Menu.Item key="models">
              <object
                className="anticon custom-icon"
                type="image/svg+xml"
                data={model_icon}
                aria-label="Icon for model tab"
              />
              <span className="nav-text">Dust Models</span>
            </Menu.Item>
            <Menu.Item key="marine">
              <object
                className="anticon custom-icon"
                type="image/svg+xml"
                data={marine_icon}
                aria-label="Icon for marine tab"
              />
              <span className="nav-text">Marine Environments</span>
            </Menu.Item>
          </Menu>
          {loginHeader}
        </Header>
        <Content className="content-container">
          <TableTab
            user={this.state.user}
            resourcePath={this.state.activeTabKey}
          />
        </Content>
        <Footer className="footer">
          <span className="footer-text">
            International Network to Encourage the Use of Monitoring and
            Forecasting Dust Products (inDust) - COST Action CA16202
            <br />
            Developed by ReACT, IAASARS, National Observatory of Athens
          </span>
          <div>
            <img className="logo" src={logo_indust} alt="inDust Logo" />
            <img className="logo" src={logo_cost} alt="COST Logo" />
            <img className="flag-eu" src={flag_eu} alt="EU Flag" />
          </div>
        </Footer>
        <LoginModal
          visible={this.state.loginVisible}
          onLogin={this.onLogin}
          onHide={this.onLoginHide}
        />
        <ChangePasswordModal
          visible={this.state.passwdVisible}
          onHide={this.onPasswdHide}
        />
      </Layout>
    );
  }

  private verifyId = async () => {
    // Verify sessionId if it exists
    if (this.state.user === undefined) {
      return;
    }

    let result;
    try {
      result = await verifyCookie();
    } catch (err) {
      message.error(
        "Connection error! Verify that your internet connection is working correctly."
      );
      return;
    }

    if (!result) {
      message.error("Session expired! Please login again.");
      this.setState({ user: undefined });
      localStorage.clear();
    }
  };

  private showLoginForm = () => this.setState({ loginVisible: true });

  private onLoginHide = () => this.setState({ loginVisible: false });

  private onLogin = (
    userId: number,
    fullName: string,
    email: string,
    isAdmin: boolean
  ): void => {
    this.setState({
      loginVisible: false,
      user: { userId, fullName, email, isAdmin },
    });

    message.success(`Logged in as ${fullName}`);

    // Save sessionId if remember me is checked
    localStorage.setItem("email", email);
    localStorage.setItem("fullName", fullName);
    localStorage.setItem("isAdmin", isAdmin ? "true" : "false");
  };

  private onLogout = async () => {
    const success = await logout();
    if (success) {
      message.success("Logged out");
    } else {
      message.error("Could not communicate with server");
    }

    this.setState({ user: undefined });
    localStorage.clear();
  };

  private onTabSelected = (item: { key: string }) => {
    this.setState({
      activeTabKey: item.key,
    });
  };

  private onPasswdHide = () => this.setState({ passwdVisible: false });
}

export default App;
